/**
 * Aba "Mensagens" da ficha da cliente, idêntica à do legado (`admin.html:2792-2803`, `5084-5103`,
 * `5743-5869`; linhas 214 a 219 do inventário).
 *
 * A conversa da consultora com a cliente, em ordem de envio: bolha própria à direita e a da cliente
 * à esquerda, anexo com link assinado de uma hora, hora `toLocaleString('pt-BR')`. Divisor
 * "N mensagens não lidas" antes da primeira não lida da cliente. A aba abre no topo da conversa,
 * como o legado na tela (defeito reproduzido, ver `carregar`); nas recargas mantém a posição, salvo
 * se estava a menos de 60 px do fim. Recarrega a cada 5 s enquanto visível, e marca como lidas as
 * mensagens da cliente que mostrou.
 *
 * A MARCA DE LIDAS AO ABRIR A FICHA (linha 216) é da moldura (frente A2): no legado ela acontece
 * porque `abrirCliente` pré-carrega esta aba. Aqui a aba também marca, a cada carga, como o legado.
 *
 * O RASCUNHO, O ANEXO E O STATUS SOBREVIVEM À TROCA DE ABA, como no legado: o
 * `carregarMensagensAdmin` só refaz a lista (`5746-5812`) e não toca no `textarea`, no
 * `arquivoAnexoAdmin` (variável global, `5744`) nem no `status-mensagem-admin`, e `resetarAbas`
 * "não limpa valor de campo nenhum" (`5959-5968`). Os três moram na memória da página
 * (`comum-a3/memoria-da-pagina.ts`), e não no componente, que desmonta quando a aba sai da tela.
 *
 * Território da frente A3. A moldura importa esta aba pelo nome `AbaMensagens`.
 */

// Estado, efeito, efeito de layout, referência e memória de função do React.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
// As peças do Chassi.
import { Botao, CampoTexto } from '@complexity/ui';
// O caminho da pasta do lead e o nome aceito pelo Storage, portados do legado.
import { caminhoArquivosCliente, sanitizarNomeArquivo } from '@studiolu/dados';
// A conversa e o anexo.
import { enviarMensagem, listarMensagens, marcarMensagensComoLidas } from '@studiolu/dados/areas/ficha-mensagens';
import { enviarArquivo, gerarLinkAssinado } from '@studiolu/dados/areas/ficha-arquivos';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O parágrafo do status, peça comum (o relógio aqui é o da memória da página).
import { LinhaDeStatus } from '../../../comum/status-temporario.js';
// O contrato da aba e o contrato de carga (a moldura lê antes de mostrar, e relê no reclique).
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// As peças da A3.
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
import { criarMemoria, criarStatusDaPagina, useMemoria } from './comum-a3/memoria-da-pagina.js';

// O intervalo da recarga automática: 5 s (`admin.html:5102`).
const INTERVALO_RECARGA_MS = 5000;
// A distância do fim, em px, abaixo da qual a recarga acompanha o fim (`admin.html:5752`).
const PERTO_DO_FIM_PX = 60;

// O `texto-nova-mensagem-admin`, o `arquivoAnexoAdmin` e o `status-mensagem-admin` do legado: DOM e
// variável da página, que nada limpa ao trocar de aba nem ao abrir outra cliente.
const rascunho = criarMemoria('');
const anexoEscolhido = criarMemoria<File | null>(null);
const statusDoEnvio = criarStatusDaPagina();

/** Uma bolha pronta para desenhar. */
interface Bolha {
  readonly id: number;
  readonly propria: boolean;
  readonly conteudo: string | null;
  // O anexo, quando há: o link (ou `null` se o link assinado falhou) e o nome.
  readonly anexo: { readonly link: string | null; readonly nome: string } | null;
  readonly hora: string;
}

/** O que a lista mostra. */
type EstadoDaLista =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'erro' }
  | { readonly tipo: 'vazia' }
  | { readonly tipo: 'conversa'; readonly bolhas: readonly Bolha[]; readonly primeiroNaoLido: number | null; readonly naoLidas: number };

/** Como rolar depois que uma recarga desenhar a lista nova. */
interface Rolagem {
  readonly pertoDoFim: boolean;
  readonly anterior: number;
}

export function AbaMensagens({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Mensagens" />;
  return <ConteudoMensagens ficha={ficha} />;
}

function ConteudoMensagens({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura, veLinksDeAdministrador } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  const [estado, setEstado] = useState<EstadoDaLista>({ tipo: 'carregando' });
  // O rascunho fica na memória da página: sair da aba e voltar encontra o mesmo texto, como o
  // `textarea` do legado, que nada limpa.
  const [texto, setTexto] = useMemoria(rascunho);
  // O anexo escolhido, como a variável `arquivoAnexoAdmin` do legado (`5744`), que também dura a página.
  const [anexo, setAnexo] = useMemoria(anexoEscolhido);
  const [status] = useMemoria(statusDoEnvio.memoria);
  const { mostrar, fixar } = statusDoEnvio;
  // A área que rola e o seletor de arquivo escondido.
  const listaRef = useRef<HTMLDivElement>(null);
  const seletorRef = useRef<HTMLInputElement>(null);
  // `true` depois da primeira carga bem sucedida, como o `lista.dataset.carregado = '1'` do legado.
  const jaCarregou = useRef(false);
  // A rolagem a aplicar quando a lista nova aparecer.
  const rolagemPendente = useRef<Rolagem | null>(null);
  // Só a leitura mais recente escreve na tela (a recarga e o envio podem se cruzar).
  const leituraAtual = useRef(0);

  // `carregarMensagensAdmin` (`admin.html:5746-5812`).
  const carregar = useCallback(async () => {
    const minha = ++leituraAtual.current;
    const lista = listaRef.current;
    // A posição ANTES de ler, como o legado mede antes de limpar a lista (`5751-5753`).
    const primeira = !jaCarregou.current;
    const pertoDoFim =
      !primeira && lista !== null && lista.scrollHeight - lista.scrollTop - lista.clientHeight < PERTO_DO_FIM_PX;
    const anterior = lista?.scrollTop ?? 0;
    // DEFEITO DO LEGADO REPRODUZIDO: a conversa some ANTES de a leitura começar (`lista.innerHTML =
    // ''`, `admin.html:5754`), então ela pisca vazia a cada recarga de 5 s e a área encolhe (a
    // altura é só `max-height`, `856-860`). Manter as bolhas até as novas chegarem seria mais
    // bonito e seria outra tela.
    setEstado({ tipo: 'carregando' });
    const resultado = await listarMensagens(dados, escopo);
    if (minha !== leituraAtual.current) return;
    if (!resultado.ok) {
      // Erro não marca a lista como carregada, e a próxima carga continua sendo "a primeira".
      setEstado({ tipo: 'erro' });
      return;
    }
    const mensagens = resultado.dados ?? [];
    if (mensagens.length === 0) {
      jaCarregou.current = true;
      setEstado({ tipo: 'vazia' });
      return;
    }
    // As não lidas da cliente: remetente que não é a consultora e sem `lida_em` (`5768`).
    const naoLidasDaCliente = mensagens.filter((m) => m.remetente !== 'consultora' && !m.lida_em);
    const bolhas: Bolha[] = [];
    for (const msg of mensagens) {
      // O link do anexo, pedido um a um como os `await` do legado (`5786`); a falha é ignorada.
      let anexoDaBolha: Bolha['anexo'] = null;
      if (msg.anexo_caminho) {
        const link = await gerarLinkAssinado(dados, msg.anexo_caminho);
        if (minha !== leituraAtual.current) return;
        // Sem o clipe do legado (`📎`, `5792`): emoji não entra na tela e o Chassi não tem o ícone.
        anexoDaBolha = { link: link.ok && link.dados ? link.dados.signedUrl : null, nome: msg.anexo_nome || 'arquivo' };
      }
      bolhas.push({
        id: msg.id,
        propria: msg.remetente === 'consultora',
        conteudo: msg.conteudo,
        anexo: anexoDaBolha,
        hora: new Date(msg.criado_em).toLocaleString('pt-BR'),
      });
      // Cada bolha entra na tela como o `appendChild` do legado (`5800`). As que não têm anexo
      // entram no mesmo instante, porque nada espera entre elas; as com anexo aparecem quando o
      // link delas chega, e é exatamente aí que o navegador desenha o parcial no legado.
      setEstado({
        tipo: 'conversa',
        bolhas: [...bolhas],
        primeiroNaoLido: naoLidasDaCliente[0]?.id ?? null,
        naoLidas: naoLidasDaCliente.length,
      });
    }
    if (minha !== leituraAtual.current) return;
    // DEFEITO DO LEGADO REPRODUZIDO (achado da A3, fora da seção 4): a primeira carga NÃO rola, e a
    // aba abre no topo da conversa, sem ir ao divisor nem ao fim. No legado o código de rolar até o
    // divisor existe (`5802-5804`), mas nunca tem efeito visível: `abrirCliente` pré-carrega esta
    // aba escondida (`5991`), o clique recarrega ANTES de mostrar a aba (`5087-5091`), e aba
    // inativa é `display:none` (`561`), onde rolar não faz nada. Medido no legado com 40 mensagens
    // e 13 não lidas: `scrollTop` 0, a 2650 px do fim, sem divisor, e assim depois de 5 s.
    rolagemPendente.current = primeira ? null : { pertoDoFim, anterior };
    jaCarregou.current = true;
    setEstado({
      tipo: 'conversa',
      bolhas,
      primeiroNaoLido: naoLidasDaCliente[0]?.id ?? null,
      naoLidas: naoLidasDaCliente.length,
    });
    // Marca como lidas as que a tela mostrou, com o instante desta carga (`5809-5811`).
    if (naoLidasDaCliente.length > 0) {
      await marcarMensagensComoLidas(
        dados,
        naoLidasDaCliente.map((m) => m.id),
        new Date().toISOString(),
      );
    }
  }, [dados, escopo]);

  // Depois de desenhar a lista nova, a rolagem do legado (`5802-5807`).
  useLayoutEffect(() => {
    const rolagem = rolagemPendente.current;
    const lista = listaRef.current;
    if (!rolagem || !lista) return;
    rolagemPendente.current = null;
    // Recarga: acompanha o fim só se já estava perto dele; senão, volta à posição anterior.
    lista.scrollTop = rolagem.pertoDoFim ? lista.scrollHeight : rolagem.anterior;
  }, [estado]);

  // A carga da aba, pelo contrato: a moldura a roda antes de mostrar a aba e de novo a cada toque
  // no botão dela, como o `await CARREGADOR_ABA_CLIENTE[...]()` do legado (`admin.html:5086`).
  const { ativa, carga } = useCarregadorDaAba(carregar);

  // A recarga a cada 5 s: nasce a cada toque na aba (`5096-5101`) e para enquanto a aba não está na
  // tela, que no legado é o `offsetParent === null` da aba escondida (`5098-5099`).
  useEffect(() => {
    if (!ativa) return undefined;
    const intervalo = window.setInterval(() => {
      void carregar();
    }, INTERVALO_RECARGA_MS);
    return () => window.clearInterval(intervalo);
  }, [ativa, carga, carregar]);

  // O `input` recém-desenhado nasce vazio, e no legado ele é o mesmo elemento do começo ao fim da
  // página: quem escolheu um anexo, saiu da aba e voltou encontra o anexo ainda escolhido.
  // `DataTransfer` é a única forma de escrever `input.files`.
  useLayoutEffect(() => {
    const seletor = seletorRef.current;
    if (!seletor) return;
    const pacote = new DataTransfer();
    if (anexo) pacote.items.add(anexo);
    seletor.files = pacote.files;
  }, [anexo]);

  // O seletor escolheu um arquivo (`admin.html:5818-5835`).
  const aoEscolherAnexo = (arquivo: File | null) => setAnexo(arquivo);

  // "remover": limpa o anexo e o seletor (`5827-5832`).
  const removerAnexo = () => {
    setAnexo(null);
    if (seletorRef.current) seletorRef.current.value = '';
  };

  // "Enviar mensagem" (`admin.html:5837-5869`).
  const enviar = async () => {
    const conteudo = texto.trim();
    if (!conteudo && !anexo) {
      fixar('Escreve uma mensagem ou anexa um arquivo.');
      return;
    }
    let anexoCaminho: string | null = null;
    let anexoNome: string | null = null;
    if (anexo) {
      // ACHADO 7 DO INVENTÁRIO, reproduzido: o anexo de mensagem NÃO tem limite de tamanho, ao
      // contrário dos outros envios, que param em 10 MB (`admin.html:5845-5851` x `7716`).
      fixar('Enviando anexo...');
      anexoNome = sanitizarNomeArquivo(anexo.name);
      // Na pasta `da-lu` da cliente, com o horário na frente para dois anexos iguais não colidirem.
      anexoCaminho = `${caminhoArquivosCliente(escopo, 'da-lu')}/${Date.now()}_${anexoNome}`;
      const envio = await enviarArquivo(dados, anexoCaminho, anexo, { upsert: true });
      // Este ponto mostra a mensagem crua do Storage depois do prefixo.
      if (!envio.ok) {
        fixar('Erro ao enviar anexo: ' + envio.erro.mensagem);
        return;
      }
    }
    fixar('Enviando...');
    const resultado = await enviarMensagem(dados, escopo, { conteudo: conteudo || '', anexoCaminho, anexoNome });
    // Este ponto do legado TRADUZ a mensagem (`traduzirErro`, `5860`).
    if (!resultado.ok) {
      fixar(resultado.erro.frase);
      return;
    }
    setTexto('');
    removerAnexo();
    mostrar('Mensagem enviada!');
    // Relê a conversa. O legado relê também a lista "Arquivos enviados por você" (`5868`); aqui
    // aquela aba lê do zero toda vez que abre, então não há lista aberta a atualizar.
    await carregar();
  };

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">Mensagens</h3>
      {/* A altura do legado: 60svh no celular e teto de 440px a partir de 768px (`admin.html:858`,
          `1613`, `1678`), e o `md` do Tailwind é exatamente 768px. */}
      <div
        ref={listaRef}
        data-testid="lista-mensagens"
        className="flex max-h-[60svh] flex-col gap-cx-2 overflow-y-auto pb-cx-4 pt-cx-1 md:max-h-[440px]"
      >
        {estado.tipo === 'erro' && <p className="text-cx-sm text-texto-suave">Não foi possível carregar as mensagens.</p>}
        {estado.tipo === 'vazia' && (
          <p className="text-cx-sm text-texto-suave">Nenhuma mensagem ainda. Inicie a conversa usando o campo abaixo.</p>
        )}
        {estado.tipo === 'conversa' &&
          estado.bolhas.map((bolha) => (
            <div key={bolha.id} className="contents">
              {bolha.id === estado.primeiroNaoLido && (
                <div
                  data-testid="divisor-nao-lidas"
                  className="my-cx-1 flex items-center gap-cx-2 self-stretch text-cx-xs uppercase tracking-wide text-texto-suave before:h-px before:flex-1 before:bg-borda before:content-[''] after:h-px after:flex-1 after:bg-borda after:content-['']"
                >
                  {estado.naoLidas === 1 ? '1 mensagem não lida' : `${estado.naoLidas} mensagens não lidas`}
                </div>
              )}
              <div
                data-testid={bolha.propria ? 'mensagem-propria' : 'mensagem-outra'}
                // A bolha ocupa até 86% no celular e 78% a partir de 768px (`admin.html:862`, `1679`).
                className={`max-w-[86%] break-words md:max-w-[78%] rounded-cx-cartao px-cx-4 py-cx-3 text-cx-sm ${
                  bolha.propria
                    ? 'self-end rounded-br-cx-sm bg-marca text-marca-contraste'
                    : 'self-start rounded-bl-cx-sm border border-borda bg-superficie text-texto'
                }`}
              >
                {bolha.conteudo && <span>{bolha.conteudo}</span>}
                {/* Link que falhou vira `#`, e `a[href="#"]` some para quem não é administrador (`3375`). */}
                {bolha.anexo && (bolha.anexo.link !== null || veLinksDeAdministrador) && (
                  <a
                    href={bolha.anexo.link ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-cx-1 block text-cx-xs underline"
                  >
                    {bolha.anexo.nome}
                  </a>
                )}
                <span className="mt-cx-1 block text-cx-xs opacity-70">{bolha.hora}</span>
              </div>
            </div>
          ))}
      </div>
      <div className="flex items-center gap-cx-3">
        {/* O seletor fica escondido e é aberto pelo "+", como no legado (`2796-2797`). */}
        <input
          ref={seletorRef}
          type="file"
          aria-label="Anexo da mensagem"
          className="hidden"
          disabled={somenteLeitura}
          onChange={(evento) => aoEscolherAnexo(evento.target.files?.[0] ?? null)}
        />
        {/* Consulta não vê botão de ação (`admin.html:3395`). */}
        {!somenteLeitura && (
          // O NOME ACESSÍVEL É "+", como no legado: lá o botão tem `title="Anexar arquivo"` e o
          // conteúdo "+" (`admin.html:2797`), e o título é descrição, não nome. Um `aria-label`
          // aqui trocaria o nome que o leitor de tela anuncia, e isso não é o legado.
          <Botao variante="secundario" tamanho="icone" title="Anexar arquivo" onClick={() => seletorRef.current?.click()}>
            +
          </Botao>
        )}
        {anexo && (
          <span data-testid="preview-anexo" className="flex min-w-0 items-center gap-cx-2 break-all text-cx-xs text-texto-suave">
            {`${anexo.name} `}
            {/* "remover" é um `a[href="#"]` VERMELHO no legado (`.preview-anexo a{ color:var(--erro) }`,
                `admin.html:895`, e o link em `5825-5832`), e some para quem não é administrador
                (`3375`). Decisão do fundador de 19/09/2026: toda ação de remover é vermelha. */}
            {veLinksDeAdministrador && (
              <a
                href="#"
                className="inline-flex min-h-toque items-center text-cx-xs text-erro underline underline-offset-4"
                onClick={(evento) => {
                  // O `#` não navega: é o `ev.preventDefault()` do legado (`5829`).
                  evento.preventDefault();
                  removerAnexo();
                }}
              >
                remover
              </a>
            )}
          </span>
        )}
      </div>
      {/* O rótulo fica visível porque o `CampoTexto` do Chassi sempre o desenha; no legado ele era
          só `aria-label` (divergência de peça, listada na entrega). Consulta: desabilitado. */}
      <CampoTexto
        rotulo="Mensagem para essa cliente"
        linhas={3}
        placeholder="Escreva uma mensagem pra essa cliente..."
        value={texto}
        disabled={somenteLeitura}
        onChange={(evento) => setTexto(evento.target.value)}
      />
      {!somenteLeitura && (
        <div>
          <Botao onClick={() => void enviar()}>Enviar mensagem</Botao>
        </div>
      )}
      <LinhaDeStatus texto={status} testid="status-mensagem-admin" />
    </section>
  );
}
